type DeviceType = 'mobile' | 'pc';

type BrowserName = 'Edge' | 'Firefox' | 'Chrome' | 'Opera' | 'Safari' | 'Unknown';

interface BrowserInfo {
    browser_type: BrowserName;
    browser_version: string;
}

type NetworkStatus = 'online' | 'offline';

interface NetworkInfo {
    type: string;
    status: NetworkStatus;
}

type OSType = 'ios' | 'android' | 'mac' | 'windows' | 'linux' | 'unknown';

interface OSInfo {
    type: OSType;
    version: string;
    isMobile: boolean;
}

// 获取设备类型
export const detectDeviceType = (): DeviceType => {
    try {
        if (typeof navigator === 'undefined') {
            return 'pc';
        }

        const userAgent = navigator.userAgent.toLowerCase();
        const mobileKeywords = [
            'android',
            'webos',
            'iphone',
            'ipad',
            'ipod',
            'blackberry',
            'windows phone'
        ];

        return mobileKeywords.some((keyword) => userAgent.includes(keyword))
            ? 'mobile'
            : 'pc';
    } catch (error) {
        console.error('Error detecting device type:', error);

        return 'mobile';
    }
};

// 获取浏览器版本类型
export const detectBrowser = (): BrowserInfo => {
    const defaultInfo: BrowserInfo = {
        browser_type: 'Unknown',
        browser_version: 'unknown'
    };

    if (typeof navigator === 'undefined') {
        return defaultInfo;
    }

    try {
        const userAgent = navigator.userAgent.toLowerCase();
        const browserRules: Array<{
            key: string;
            regex: RegExp;
            name: BrowserName;
        }> = [
            { key: 'edg', regex: /edg\/([\d.]+)/, name: 'Edge' }, // Edge 浏览器
            { key: 'firefox', regex: /firefox\/([\d.]+)/, name: 'Firefox' }, // Firefox 浏览器
            { key: 'chrome', regex: /chrome\/([\d.]+)/, name: 'Chrome' }, // Chrome 浏览器
            { key: 'opera', regex: /opera.([\d.]+)/, name: 'Opera' }, // Opera 浏览器
            { key: 'safari', regex: /version\/([\d.]+).*safari/, name: 'Safari' } // Safari 浏览器
        ];

        for (const rule of browserRules) {
            const matchResult = userAgent.match(rule.regex);

            if (matchResult && matchResult[1]) {
                return {
                    browser_type: rule.name,
                    browser_version: matchResult[1]
                };
            }
        }

        return defaultInfo;
    } catch (error) {
        console.error('Error detecting browser:', error);

        return defaultInfo;
    }
};

// 获取网络类型
export const detectNetworkInfo = (): NetworkInfo => {
    const defaultInfo: NetworkInfo = {
        type: 'unknown',
        status: 'offline'
    };

    if (typeof navigator === 'undefined') {
        return defaultInfo;
    }

    try {
        const isOnline = navigator.onLine;
        const connection =
            (navigator as Navigator & {
                connection?: { type?: string };
                mozConnection?: { type?: string };
                webkitConnection?: { type?: string };
            }).connection ??
            (navigator as any).mozConnection ??
            (navigator as any).webkitConnection;

        const networkStatus: NetworkStatus = isOnline ? 'online' : 'offline';
        const networkType =
            typeof connection?.type === 'string' ? connection.type : 'unknown';

        return {
            type: networkType,
            status: networkStatus
        };
    } catch (error) {
        console.error('Error detecting network info:', error);

        return {
            type: 'unknown',
            status: navigator.onLine ? 'online' : 'offline'
        };
    }
};

// 获取操作系统
export const detectOS = (): OSInfo => {
    if (typeof window === 'undefined' || typeof window.navigator === 'undefined') {
        return {
            type: 'unknown',
            version: 'unknown',
            isMobile: false
        };
    }

    try {
        const userAgent = window.navigator.userAgent.toLowerCase();
        const platform = window.navigator.platform?.toLowerCase() || '';

        // iOS 检测
        if (
            /iphone|ipad|ipod/.test(userAgent) ||
            platform.includes('iphone') ||
            platform.includes('ipad') ||
            platform.includes('ipod')
        ) {
            const versionMatch = userAgent.match(/os (\d+)_(\d+)_?(\d+)?/);
            const version = versionMatch
                ? `${versionMatch[1]}.${versionMatch[2]}.${versionMatch[3] || 0}`
                : 'unknown';

            return {
                type: 'ios',
                version,
                isMobile: true
            };
        }

        // Android 检测
        if (/android/.test(userAgent)) {
            const versionMatch = userAgent.match(/android (\d+)\.?(\d+)?\.?(\d+)?/);
            const version = versionMatch
                ? `${versionMatch[1]}.${versionMatch[2] || 0}.${versionMatch[3] || 0}`
                : 'unknown';

            return {
                type: 'android',
                version,
                isMobile: true
            };
        }

        // macOS 检测
        if (
            /macintosh|mac os x|macintel/.test(userAgent) ||
            platform.includes('mac') ||
            /mac/.test(platform)
        ) {
            const versionMatch = userAgent.match(/mac os x (\d+)[._](\d+)[._]?(\d+)?/);
            const version = versionMatch
                ? `${versionMatch[1]}.${versionMatch[2]}.${versionMatch[3] || 0}`
                : 'unknown';

            return {
                type: 'mac',
                version,
                isMobile: false
            };
        }

        // Windows 检测
        if (
            /windows|win32|win64/.test(userAgent) ||
            platform.includes('win') ||
            /win/.test(platform)
        ) {
            let version = 'unknown';

            // Windows 版本检测
            if (/windows nt 10\.0/.test(userAgent)) {
                version = '10';
            } else if (/windows nt 6\.3/.test(userAgent)) {
                version = '8.1';
            } else if (/windows nt 6\.2/.test(userAgent)) {
                version = '8';
            } else if (/windows nt 6\.1/.test(userAgent)) {
                version = '7';
            } else if (/windows nt 6\.0/.test(userAgent)) {
                version = 'Vista';
            } else if (/windows nt 5\.1/.test(userAgent)) {
                version = 'XP';
            } else if (/windows nt 5\.0/.test(userAgent)) {
                version = '2000';
            }

            return {
                type: 'windows',
                version,
                isMobile: false
            };
        }

        // Linux 检测
        if (
            /linux/.test(userAgent) ||
            platform.includes('linux') ||
            /linux/.test(platform)
        ) {
            return {
                type: 'linux',
                version: 'unknown',
                isMobile: false
            };
        }

        // 默认返回未知
        return {
            type: 'unknown',
            version: 'unknown',
            isMobile: false
        };
    } catch (error) {
        console.error('Error detecting OS:', error);

        return {
            type: 'unknown',
            version: 'unknown',
            isMobile: false
        };
    }
};

/** 获取操作系统版本字符串，供测试或外部使用 */
export const getOSVersion = (): string => {
    return detectOS().version;
};
